/**
 * @module ActionableMessageHttpChannelAdapter
 */

import {
    ActivityRequest,
    ActivityResponse,
    ActivityStatus,
    HttpChannelAdapter,
    ActivityInvocationTrigger,
} from 'adaptivecards-v2';

import {
    MessageContext,
    CardDetails,
    ExecuteV2ActionResponse,
    lazyParseCardFromStringOrObject,
    ActionableMessageTelemetryHandler,
    ElementDetails,
} from 'owa-actionable-message-v2';
import { getUserConfiguration } from 'owa-session-store';
import { getClientAuthToken } from 'owa-connectors';
import { actionGenericError } from 'owa-locstrings/lib/strings/actiongenericerror.locstring.json';
import { actionGenericSuccess } from 'owa-locstrings/lib/strings/actiongenericsuccess.locstring.json';
import loc from 'owa-localize';

import { SINGLE_CLICK_POST_ACTION_CLICKED, AUTO_INVOKE_ACTION_CLICKED } from '../constants';

/**
 * @enum
 * @property {string} Completed - A property represents a completed state
 * @property {string} Failed - A property represents a failed state
 */

enum RequestStatus {
    Completed = 'Completed',
    Failed = 'Failed',
}

/**
 * @class ActionableMessageHttpChannelAdapter
 * A class to represent structure of http channel adapter for Actionable Messages
 * @implements HttpChannelAdapter
 */

export default class ActionableMessageHttpChannelAdapter extends HttpChannelAdapter {
    /**
     * @private
     * A member to hold the card details
     */

    private cardDetails: CardDetails;

    /**
     * @private
     * A member to hold the message context object
     */

    private messageContext: MessageContext;

    /**
     * @private
     * A member to hold a callback on card details updated
     * @param {object} card - A card data
     * @returns nothing
     */

    private onCardDetailsUpdated: (card: object) => void;

    /**
     * @private
     * A member to hold a reference to telemetry handler
     */

    private telemetryHandler: ActionableMessageTelemetryHandler;

    /**
     * @private
     * @async
     * A member to parse the card details from the response string or object
     * @param {ExecuteV2ActionResponse} response - A response object
     * @returns A promise that resolves to card details
     */

    private parseCardFromStringOrObject = (
        response: ExecuteV2ActionResponse
    ): Promise<CardDetails> => {
        return lazyParseCardFromStringOrObject
            .import()
            .then(parseCard =>
                parseCard(
                    this.cardDetails,
                    this.cardDetails.connectorSenderGuid,
                    response.newCardPayload,
                    response.newCardSignature,
                    true,
                    this.cardDetails.cardId
                )
            );
    };

    /**
     * @private
     * A method to parse the action response json to a structure known to Actionable Messages package
     * @param {any} response - A response json object
     * @param {boolean} responseOk - A boolean representing whether the action succeeded or not
     * @returns A parsed response
     */

    private parseResponse(response: any, responseOk: boolean): ExecuteV2ActionResponse {
        const success = responseOk && response.status === RequestStatus.Completed;
        let displayMessage: string;
        if (response.displayMessage) {
            displayMessage = response.displayMessage;
        } else if (response.errorMessage) {
            displayMessage = response.errorMessage;
        } else if (success) {
            displayMessage = loc(actionGenericSuccess);
        } else {
            displayMessage = loc(actionGenericError);
        }

        return {
            status: success ? RequestStatus.Completed : RequestStatus.Failed,
            displayMessage: displayMessage,
            newCardPayload: response.refreshCardSerialized,
            newCardSignature: response.refreshCardSignature,
            loginUrl: response.loginUrl,
        };
    }

    /**
     * @private
     * To get event name of an action for telemetry
     * @param {ActivityRequest} request - The request object
     * @returns An event name for telemetry
     */
    private getActionTelemetryEventName(request: ActivityRequest): string {
        /**
         * TODO: Find a proper way to identify action execution happening for an inline adaptive card
         */
        const isRefresh = request.activity.value.trigger === ActivityInvocationTrigger.Automatic;
        return isRefresh ? AUTO_INVOKE_ACTION_CLICKED : SINGLE_CLICK_POST_ACTION_CLICKED;
    }

    /**
     * @public
     * @constructor
     * To create an instance of ActionableMessageHttpChannelAdapter
     * @param {string} url - A request url string
     * @param {CardDetails} cardDetails - A card details object
     * @param {MessageContext} messageContext - A message context object
     * @param {(card: object) => void} onCardDetailsUpdated - A callback when card details updated
     * @param {ActionableMessageTelemetryHandler} telemetryHandler - A telemetry handler object
     * @returns An instance of ActionableMessageHttpChannelAdapter
     */

    public constructor(
        url: string,
        cardDetails: CardDetails,
        messageContext: MessageContext,
        onCardDetailsUpdated: (card: object) => void,
        telemetryHandler: ActionableMessageTelemetryHandler
    ) {
        super(url);
        this.cardDetails = cardDetails;
        this.messageContext = messageContext;
        this.onCardDetailsUpdated = onCardDetailsUpdated;
        this.telemetryHandler = telemetryHandler;
    }

    /**
     * @public
     * @async
     * To send out the http action execute request
     * @param {ActivityRequest} request - A request object for action execute
     * @returns A promise that resolves to an Activity response
     */

    public async sendRequestAsync(request: ActivityRequest): Promise<ActivityResponse> {
        try {
            const channelData = {
                connectorSenderGuid: this.cardDetails.connectorSenderGuid,
                /**
                 * TODO: Fill in input params
                 */
                inputParameters: [],
                senderAddress: this.messageContext.senderAddress,
                smtpAddressesSerialized: this.cardDetails.smtpAddressesSerialized,
                adaptiveCardSignature: this.cardDetails.cardSignature,
                adaptiveCardHash: this.cardDetails.cardHash,
                /**
                 * TODO: Fill in client telemetry object
                 */
                clientTelemetry: {},
            };

            request.activity.channelData = channelData;

            const userConfiguration = getUserConfiguration();
            const userPrincipalName = userConfiguration.SessionSettings?.UserPrincipalName;
            const userEmail = userConfiguration.SessionSettings?.UserEmailAddress;

            request.activity.from = {
                id: userPrincipalName ? userPrincipalName : userEmail,
            };

            const token = await getClientAuthToken();
            if (!token) {
                const emptyTokenError = new Error(`Token is empty: ${token}`);
                emptyTokenError.name = 'EmptyTokenError';
                throw emptyTokenError;
            }

            const actionEventName = this.getActionTelemetryEventName(request);
            const action = request.action;
            const elementDetails: ElementDetails = {
                id: action.id,
                type: action.getJsonTypeName(),
            };
            this.telemetryHandler.logTelemetryProvider(
                this.cardDetails,
                actionEventName,
                null /*actionContext*/,
                elementDetails
            );

            let response = await fetch(this.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(request.activity),
            });

            const responseJson = await response.json();
            const parsedResponse = this.parseResponse(responseJson, response.ok);
            if (parsedResponse.newCardPayload) {
                const newCardDetails = await this.parseCardFromStringOrObject(parsedResponse);
                this.cardDetails = newCardDetails;
                this.onCardDetailsUpdated([newCardDetails]);
            }

            let responseToLibrary: ActivityResponse;
            if (response.ok && parsedResponse.status === RequestStatus.Completed) {
                responseToLibrary = {
                    request: request,
                    status: ActivityStatus.Success,
                    content: parsedResponse.newCardPayload
                        ? parsedResponse.newCardPayload
                        : parsedResponse.displayMessage,
                };
            } else {
                responseToLibrary = {
                    request: request,
                    /**
                     * Hack for loginUrl to work, until the fix is done in AdaptiveCard library
                     */
                    status: parsedResponse.loginUrl
                        ? ActivityStatus.Success
                        : ActivityStatus.Failure,
                    content: parsedResponse.loginUrl
                        ? JSON.stringify({
                              loginUrl: parsedResponse.loginUrl,
                              type: 'Activity.InvocationError.Unauthorized',
                          })
                        : parsedResponse.displayMessage,
                };
            }
            return responseToLibrary;
        } catch (error) {
            throw error;
        }
    }
}
